import { Tag } from '@trustoo/components/Tag/v2'
import { Textarea } from '@trustoo/components/Textarea/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

// ---------- Textarea (v2) showcase ----------
// Generated from the Figma "Textarea input field" (5:15323) ComponentSet.

const TEXTAREA_USAGE = `import { Textarea } from '@trustoo/components/Textarea/v2'

<Textarea label="Message" placeholder="Leave a message" onChange={onChange} />

// Hint text + required asterisk
<Textarea label="Bio" required hintText="Max 280 characters." />

// Lock the size (no drag handle)
<Textarea label="Message" resizeHandle={false} />

// Tags variant — chip row above the text area (real Tag component)
<Textarea variant="tags" label="Tags" tags={tags.map((t) => (
    <Tag key={t} size="sm" onClose={() => remove(t)}>{t}</Tag>
))} />

// Validation / destructive
<Textarea label="Message" error="This field is required." />
<Textarea label="Message" destructive />`

export const TextareaShowcase: React.FC = () => (
    <Section
        title="Textarea (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Textarea input field</span>{' '}
                set (ComponentSet <InlineCode>5:15323</InlineCode>). Import v2 explicitly; the{' '}
                <InlineCode>@trustoo/components/Textarea</InlineCode> barrel resolves to{' '}
                <InlineCode>v1</InlineCode> (the legacy <InlineCode>TextAreaField</InlineCode>)
                until v2 is promoted.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="grid gap-spacing-xl sm:grid-cols-2">
                <div className="space-y-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Textarea
                        label="Message"
                        placeholder="Leave a message"
                        hintText="Placeholder"
                    />
                    <Textarea
                        label="Message"
                        value="Thanks for the quick turnaround on this!"
                        hintText="Filled"
                    />
                    <Textarea
                        label="Message"
                        placeholder="Leave a message"
                        state="focused"
                        hintText="Focused (forced)"
                    />
                </div>
                <div className="space-y-spacing-xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Textarea
                        label="Message"
                        placeholder="Leave a message"
                        disabled
                        hintText="Disabled"
                    />
                    <Textarea
                        label="Message"
                        value="Too short"
                        error="Please enter at least 20 characters."
                    />
                    <Textarea
                        label="Message"
                        placeholder="Leave a message"
                        destructive
                        hintText="Destructive"
                    />
                </div>
            </div>

            <div className="rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                <Textarea
                    variant="tags"
                    label="Tags"
                    placeholder="Add tags"
                    tags={
                        <>
                            <Tag size="sm" onClose={() => undefined}>
                                Design
                            </Tag>
                            <Tag size="sm" onClose={() => undefined}>
                                Software
                            </Tag>
                            <Tag size="sm" onClose={() => undefined}>
                                Research
                            </Tag>
                        </>
                    }
                    hintText="tags variant — real Tag component"
                />
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={TEXTAREA_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>variant</InlineCode> (default · tags),{' '}
                    <InlineCode>state</InlineCode> (placeholder · default · focused · disabled),{' '}
                    <InlineCode>destructive</InlineCode>, <InlineCode>label</InlineCode>,{' '}
                    <InlineCode>hintText</InlineCode>, <InlineCode>required</InlineCode>,{' '}
                    <InlineCode>helpIcon</InlineCode>, <InlineCode>resizeHandle</InlineCode>,{' '}
                    <InlineCode>tags</InlineCode>, <InlineCode>error</InlineCode>, and{' '}
                    <InlineCode>withErrorIcon</InlineCode>. All native{' '}
                    <InlineCode>&lt;textarea&gt;</InlineCode> props pass through and the component
                    forwards its ref.
                </p>
            </div>
        </div>
    </Section>
)
